/**
 * Decision Log
 *
 * The organizational record of what has been decided, why, under what signal
 * it could change, and what came before.
 *
 * This page bridges two surfaces:
 *
 *   Forming a decision (/brief) — where decisions are formed (The Frame, proposals, record)
 *   Decision Log (here)         — where promoted decisions live alongside health tracking
 *
 * The three modes a decision can be in:
 *   Creates   — net-new commitment
 *   Adds to   — adds to an existing plan entry
 *   Changes   — modifies a prior commitment (prior decision superseded)
 *   Reverses  — cancels a prior commitment
 *
 * Route: /prioritization/decisions/brief
 */

import { useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Printer,
  Calendar,
  ChevronDown,
  AlertTriangle,
  RefreshCw,
  ArrowUpRight,
  ArrowRight,
  PlusCircle,
  BookOpen,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { HEALTH_CONFIG, summarizeHealth } from '../../lib/decisionHealth';
import type { Decision } from '../../types/prioritization/decisions';

// ── Constants ─────────────────────────────────────────────────────────────────

const IMPACT_CONFIG: Record<Decision['impact'], { label: string; color: string }> = {
  high:   { label: 'High',   color: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  medium: { label: 'Medium', color: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  low:    { label: 'Low',    color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(iso));
}

function todayLabel(): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  }).format(new Date());
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '…';
}

// ── Badges ────────────────────────────────────────────────────────────────────

function ImpactBadge({ impact }: { impact: Decision['impact'] }) {
  const cfg = IMPACT_CONFIG[impact];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function HealthBadge({ status }: { status: Decision['healthStatus'] }) {
  if (!status) return null;
  const cfg = HEALTH_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── Section: Decisions from Decision Log ─────────────────────────────────────

interface DecisionLogSectionProps {
  decisions: Decision[];
  poolFilter: string;
}

function DecisionLogSection({ decisions, poolFilter }: DecisionLogSectionProps) {
  const decisionLogDecisions = useMemo(() =>
    decisions.filter((d) => d.originModule === 'decision_brief'),
    [decisions]
  );

  const filtered = useMemo(() =>
    poolFilter === 'all'
      ? decisionLogDecisions
      : decisionLogDecisions.filter((d) => d.itemPoolId === poolFilter),
    [decisionLogDecisions, poolFilter]
  );

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
            Decisions from the Decision Log
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Promoted from formed briefs — with full framing, rationale, and signal for change
          </p>
        </div>
        <Link
          to="/brief"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-xs font-medium hover:bg-[var(--slate)] transition-colors"
        >
          <PlusCircle className="h-3.5 w-3.5" />
          Form a new decision
        </Link>
      </div>

      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--rule)] p-8 text-center">
          <FileText className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-medium text-[var(--text-sec)] mb-1">No decisions from the Decision Log yet</p>
          <p className="text-xs text-[var(--text-ter)] mb-4 max-w-sm mx-auto">
            Decisions show up here once they are formed, with The Frame locked, proposals collected, and full rationale documented.
          </p>
          <Link
            to="/brief"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
          >
            Open the Decision Log <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => {
            const briefId = d.source?.replace('brief:', '');

            return (
              <div key={d.id} className="border border-[var(--rule)] p-4 bg-[var(--surface)]/50 hover:bg-[var(--surface)] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <ImpactBadge impact={d.impact} />
                      <HealthBadge status={d.healthStatus} />
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)]">
                        <BookOpen className="h-3 w-3" />
                        Decision Log
                      </span>
                    </div>
                    <p className="text-sm font-medium text-[var(--text-pri)] leading-snug">{d.title}</p>
                    {d.rationaleReasoning && (
                      <p className="text-xs text-[var(--text-sec)] mt-1">{truncate(d.rationaleReasoning, 120)}</p>
                    )}
                    {d.reconsiderationConditions && (
                      <div className="flex items-start gap-1.5 mt-2">
                        <AlertTriangle className="h-3 w-3 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-[var(--text-ter)]">
                          <span className="font-medium text-[var(--text-sec)]">Signal: </span>
                          {truncate(d.reconsiderationConditions, 100)}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-xs text-[var(--text-ter)]">
                    <span>{formatDate(d.decidedAt)}</span>
                    {briefId && (
                      <Link
                        to={`/brief/${briefId}`}
                        className="inline-flex items-center gap-1 text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
                        title="View source brief"
                      >
                        Brief <ArrowUpRight className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className="pt-2">
            <Link
              to="/brief"
              className="inline-flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
            >
              Open Decision Log <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section A — Active Priorities ─────────────────────────────────────────────

interface ActivePrioritiesProps {
  poolFilter: string;
}

function ActivePriorities({ poolFilter }: ActivePrioritiesProps) {
  const decisions = useDecisionsStore((s) => s.decisions);
  const itemPools = useItemPoolsStore((s) => s.itemPools);

  const visiblePools = useMemo(
    () => poolFilter === 'all' ? itemPools : itemPools.filter((p) => p.id === poolFilter),
    [itemPools, poolFilter],
  );

  const poolDecisions = useMemo(() => {
    const map: Record<string, Decision[]> = {};
    for (const pool of visiblePools) {
      map[pool.id] = decisions.filter(
        (d) => d.itemPoolId === pool.id && d.status === 'active',
      );
    }
    return map;
  }, [decisions, visiblePools]);

  const hasAny = visiblePools.some((p) => poolDecisions[p.id]?.length > 0);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-4">
        A — Active Priorities
      </p>
      {!hasAny ? (
        <p className="text-sm text-[var(--text-ter)] italic">
          No active priorities with documented decisions.
        </p>
      ) : (
        <div className="space-y-6">
          {visiblePools.map((pool) => {
            const poolDecs = poolDecisions[pool.id] ?? [];
            if (poolDecs.length === 0) return null;
            return (
              <div key={pool.id}>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[var(--slate)] flex-shrink-0" />
                  {pool.name}
                  <span className="ml-auto text-xs font-normal text-[var(--text-ter)]">
                    {poolDecs.length} decision{poolDecs.length !== 1 ? 's' : ''}
                  </span>
                </h3>
                <div className="space-y-3">
                  {poolDecs.map((d) => (
                    <div key={d.id} className="border border-[var(--rule)] p-4 bg-[var(--surface)]/50">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-medium text-[var(--text-pri)] leading-snug flex-1">
                          {d.title}
                        </p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <ImpactBadge impact={d.impact} />
                          <HealthBadge status={d.healthStatus} />
                        </div>
                      </div>
                      {d.rationaleContext && (
                        <p className="text-sm text-[var(--text-sec)] mt-2 leading-relaxed">
                          {truncate(d.rationaleContext, 120)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Section B — Decision Health ───────────────────────────────────────────────

function HealthOverview({ poolFilter }: { poolFilter: string }) {
  const allDecisions = useDecisionsStore((s) => s.decisions);
  const itemPools    = useItemPoolsStore((s) => s.itemPools);

  const decisions = useMemo(() =>
    poolFilter === 'all' ? allDecisions : allDecisions.filter((d) => d.itemPoolId === poolFilter),
    [allDecisions, poolFilter]
  );

  const summary = useMemo(() => summarizeHealth(decisions), [decisions]);
  const staleAndOrphaned = useMemo(
    () => decisions.filter((d) => d.healthStatus === 'stale' || d.healthStatus === 'orphaned'),
    [decisions],
  );

  const poolNameFor = (d: Decision): string => {
    if (!d.itemPoolId) return '—';
    return itemPools.find((p) => p.id === d.itemPoolId)?.name ?? '—';
  };

  const statCards = [
    { key: 'fresh',    label: 'Fresh',    count: summary.fresh,    bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', border: 'border-[var(--green)]' },
    { key: 'aging',    label: 'Aging',    count: summary.aging,    bg: 'bg-[var(--mustard-dim)]',   text: 'text-[var(--mustard)]',   border: 'border-[var(--mustard)]' },
    { key: 'stale',    label: 'Stale',    count: summary.stale,    bg: 'bg-[var(--coral-dim)]',     text: 'text-[var(--coral)]',     border: 'border-[var(--coral)]' },
    { key: 'orphaned', label: 'Orphaned', count: summary.orphaned, bg: 'bg-[var(--surface)]',   text: 'text-[var(--text-sec)]',   border: 'border-[var(--rule)]' },
  ] as const;

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-4">
        B — Decision Health Overview
      </p>
      {summary.total === 0 ? (
        <p className="text-sm text-[var(--text-ter)] italic">No decisions to analyze.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {statCards.map((s) => {
              const pct = summary.total > 0 ? Math.round((s.count / summary.total) * 100) : 0;
              return (
                <div key={s.key} className={`border ${s.border} ${s.bg}  p-4`}>
                  <div className={`text-3xl font-bold ${s.text}`}>{s.count}</div>
                  <div className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mt-1">{s.label}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{pct}% of total</div>
                </div>
              );
            })}
          </div>

          <div className="border border-[var(--rule)] p-5 mb-6 flex items-center gap-5">
            <div>
              <div className="text-3xl font-bold text-[var(--text-pri)]">{summary.coverageScore}%</div>
              <div className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mt-0.5">Rationale Coverage</div>
            </div>
            <div className="flex-1">
              <div className="h-2.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                <div className="h-full bg-[var(--green)] rounded-full transition-all" style={{ width: `${summary.coverageScore}%` }} />
              </div>
              <p className="text-xs text-[var(--text-ter)] mt-1.5">
                {summary.total - summary.orphaned} of {summary.total} decisions have documented rationale
              </p>
            </div>
          </div>

          {staleAndOrphaned.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-3">
                Needs Attention ({staleAndOrphaned.length})
              </p>
              <div className="border border-[var(--rule)] overflow-hidden">
                <table className="min-w-full divide-y divide-[var(--rule)]">
                  <thead className="bg-[var(--surface)]">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Decision</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide whitespace-nowrap hidden md:table-cell">Focus List</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Status</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--rule)] bg-[var(--bg-raised)]">
                    {staleAndOrphaned.map((d) => (
                      <tr key={d.id} className="hover:bg-[var(--surface)]/50 transition-colors">
                        <td className="px-4 py-3 text-sm text-[var(--text-pri)] font-medium leading-snug max-w-xs">
                          {d.title}
                          {/* Focus List is drawn from md up and Last Updated from sm up; below, they read here (audit #43). */}
                          <span className="md:hidden block mt-0.5 text-xs font-normal text-[var(--text-ter)]">
                            {poolNameFor(d) !== '—' && poolNameFor(d)}
                            <span className="sm:hidden">
                              {poolNameFor(d) !== '—' ? ' · ' : ''}Updated {formatDate(d.updatedAt)}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--text-ter)] hidden md:table-cell whitespace-nowrap">{poolNameFor(d)}</td>
                        <td className="px-4 py-3 whitespace-nowrap"><HealthBadge status={d.healthStatus} /></td>
                        <td className="px-4 py-3 text-sm text-[var(--text-ter)] hidden sm:table-cell whitespace-nowrap">{formatDate(d.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section C — Signal for Change ────────────────────────────────────────────

function ReconsiderationConditions({ poolFilter }: { poolFilter: string }) {
  const allDecisions = useDecisionsStore((s) => s.decisions);
  const itemPools    = useItemPoolsStore((s) => s.itemPools);

  const decisions = useMemo(() => {
    const base = poolFilter === 'all' ? allDecisions : allDecisions.filter((d) => d.itemPoolId === poolFilter);
    return base.filter((d) => d.reconsiderationConditions && d.reconsiderationConditions.trim().length > 0);
  }, [allDecisions, poolFilter]);

  const poolNameFor = (d: Decision): string => {
    if (!d.itemPoolId) return '—';
    return itemPools.find((p) => p.id === d.itemPoolId)?.name ?? '—';
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-4">
        C — Signal for Change
      </p>
      {decisions.length === 0 ? (
        <p className="text-sm text-[var(--text-ter)] italic">
          No signals for change have been documented. Add them when recording a decision.
        </p>
      ) : (
        <div className="space-y-4">
          {decisions.map((d) => (
            <div key={d.id} className="border border-[var(--mustard)] p-4 bg-[var(--mustard-dim)]/40">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-sm font-medium text-[var(--text-pri)] leading-snug flex-1">{d.title}</p>
                <span className="text-xs text-[var(--text-ter)] whitespace-nowrap flex-shrink-0">{poolNameFor(d)}</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[var(--text-sec)] leading-relaxed">{d.reconsiderationConditions}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function PlanOfRecordPage() {
  const fetchDecisions = useDecisionsStore((s) => s.fetchDecisions);
  const syncState      = useDecisionsStore((s) => s.syncState);
  const decisions      = useDecisionsStore((s) => s.decisions);
  const itemPools      = useItemPoolsStore((s) => s.itemPools);

  const [poolFilter, setPoolFilter] = useState<string>('all');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => { fetchDecisions(); }, [fetchDecisions]);

  const selectedPoolName = useMemo(() => {
    if (poolFilter === 'all') return 'All Focus Lists';
    return itemPools.find((p) => p.id === poolFilter)?.name ?? 'All Focus Lists';
  }, [poolFilter, itemPools]);

  const decisionLogCount = decisions.filter((d) => d.originModule === 'decision_brief').length;

  return (
    <>
      <style>{`@media print { .no-print { display: none; } }`}</style>

      <div className="space-y-5">
        <PageHeader
          icon={<FileText className="h-4 w-4 text-[var(--text-ter)]" />}
          iconBg="bg-[var(--slate-dim)]"
          title="Decision Log"
          subtitle="What has been decided, why, and under what signal it can change"
          actions={
            <div className="flex items-center gap-2 no-print">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium">
                <Calendar className="h-3.5 w-3.5" />
                {todayLabel()}
              </div>

              {/* Focus List filter */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setFilterOpen((o) => !o)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-raised)] border border-[var(--rule)] text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                >
                  {selectedPoolName}
                  <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                </button>
                {filterOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
                    <div className="absolute right-0 z-20 mt-1 w-52 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg py-1 text-sm">
                      <button
                        type="button"
                        onClick={() => { setPoolFilter('all'); setFilterOpen(false); }}
                        className={`w-full text-left px-3 py-2 transition-colors ${poolFilter === 'all' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]'}`}
                      >
                        All Focus Lists
                      </button>
                      {itemPools.map((pool) => (
                        <button
                          key={pool.id}
                          type="button"
                          onClick={() => { setPoolFilter(pool.id); setFilterOpen(false); }}
                          className={`w-full text-left px-3 py-2 transition-colors truncate ${poolFilter === pool.id ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] font-medium' : 'text-[var(--text-sec)] hover:bg-[var(--surface)]'}`}
                        >
                          {pool.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-raised)] border border-[var(--rule)] text-[var(--text-sec)] text-xs font-medium hover:bg-[var(--surface)] transition-colors"
              >
                <Printer className="h-3.5 w-3.5" />
                Print
              </button>

              <Link
                to="/brief"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-white text-xs font-medium hover:bg-[var(--slate)] transition-colors"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                Decision Log
                {decisionLogCount > 0 && (
                  <span className="bg-[var(--slate)] text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-medium ml-0.5">
                    {decisionLogCount}
                  </span>
                )}
              </Link>
            </div>
          }
        />

        {syncState.isLoading && (
          <div className="flex items-center justify-center h-24">
            <RefreshCw className="h-5 w-5 text-[var(--text-ter)] animate-spin" />
          </div>
        )}

        {!syncState.isLoading && (
          <div className="space-y-5">
            {/* Decision Log section is always first — formations feed the plan */}
            <DecisionLogSection decisions={decisions} poolFilter={poolFilter} />
            <ActivePriorities poolFilter={poolFilter} />
            <HealthOverview poolFilter={poolFilter} />
            <ReconsiderationConditions poolFilter={poolFilter} />
          </div>
        )}
      </div>
    </>
  );
}
